(() => {
  const SELECTOR = '[data-h2rv]';

  function init(root) {
    if (!root || root.dataset.h2rvReady === '1') return;

    const viewport = root.querySelector('[data-h2rv-viewport]');
    const track = root.querySelector('[data-h2rv-track]');
    const prev = root.querySelector('[data-h2rv-prev]');
    const next = root.querySelector('[data-h2rv-next]');
    const dots = root.querySelector('[data-h2rv-dots]');
    const originals = Array.from(track?.children || []).filter(el => el.matches('[data-h2rv-slide]'));
    if (!viewport || !track || originals.length < 1) return;

    root.dataset.h2rvReady = '1';

    const loop = root.dataset.loop !== 'false' && originals.length > 1;
    // New reference setting defaults to motion ON, even on an older JSON template
    // where this setting is not yet persisted.
    const autoplay = root.dataset.autoplay !== 'false' && originals.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const pauseHover = root.dataset.pauseHover === 'true';
    const showProgress = root.dataset.showProgress !== 'false';
    const speed = Math.max(300, Number(root.dataset.speed) || 600);
    const delay = Math.max(2500, Number(root.dataset.delay) || 3500);

    // Need several clones because the reference layout shows ~3 full cards plus peeks.
    const cloneCount = loop ? Math.min(4, originals.length) : 0;
    let index = cloneCount;
    let logical = 0;
    let animating = false;
    let unlockTimer = null;
    let timer = null;
    let resizeTimer = null;
    let dragging = false;
    let startX = 0;
    let lastX = 0;
    let startTranslate = 0;
    let currentTranslate = 0;
    let step = 1;
    let peek = 0;

    function cleanClone(node) {
      node.removeAttribute('id');
      node.removeAttribute('data-block-id');
      node.removeAttribute('data-shopify-editor-block');
      node.removeAttribute('shopify-design-mode');
      node.setAttribute('aria-hidden', 'true');
      node.dataset.h2rvClone = '1';
      node.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
    }

    if (loop) {
      const before = originals.slice(-cloneCount).map(el => el.cloneNode(true));
      const after = originals.slice(0, cloneCount).map(el => el.cloneNode(true));
      before.forEach(cleanClone);
      after.forEach(cleanClone);
      before.forEach(el => track.insertBefore(el, originals[0]));
      after.forEach(el => track.appendChild(el));
    }

    function slides() {
      return Array.from(track.children).filter(el => el.matches('[data-h2rv-slide]'));
    }

    function makeDots() {
      if (!dots) return;
      dots.innerHTML = '';
      originals.forEach((_, i) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'h2rv__dot';
        button.setAttribute('aria-label', `Go to slide ${i + 1}`);
        button.addEventListener('click', () => {
          stop();
          goLogical(i);
          schedule();
        });
        dots.appendChild(button);
      });
      updateDots();
    }

    function clearProgress() {
      if (!dots) return;
      dots.querySelectorAll('.h2rv__dot').forEach(dot => {
        dot.classList.remove('is-progressing');
        dot.style.removeProperty('--h2rv-progress-duration');
      });
    }

    function startProgress(duration = delay) {
      clearProgress();
      if (!dots || !showProgress || !autoplay || dragging || document.hidden) return;
      const activeDot = dots.querySelector('.h2rv__dot.is-active');
      if (!activeDot) return;
      activeDot.style.setProperty('--h2rv-progress-duration', `${duration}ms`);
      // Force a new animation cycle each time the slide changes / timer restarts.
      void activeDot.offsetWidth;
      activeDot.classList.add('is-progressing');
    }

    function updateDots() {
      if (!dots) return;
      dots.querySelectorAll('.h2rv__dot').forEach((dot, i) => {
        const active = i === logical;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
        if (!active) {
          dot.classList.remove('is-progressing');
          dot.style.removeProperty('--h2rv-progress-duration');
        }
      });
    }

    function measure() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const gap = parseFloat(getComputedStyle(track).gap || '0') || 0;
      const p = mobile
        ? Math.max(14, viewport.clientWidth * 0.045)
        : Math.max(24, Math.min(54, viewport.clientWidth * 0.027));
      peek = p;

      let width;
      if (mobile) {
        const requested = Number(root.dataset.mobileWidth) || 310;
        width = Math.min(requested, viewport.clientWidth * 0.82);
      } else {
        // Reference: 3 full cards, with a visible next/previous edge.
        width = Math.max(280, (viewport.clientWidth - (peek * 2) - (gap * 3.15)) / 3.15);
      }

      root.style.setProperty('--h2rv-card-w', `${width}px`);
      root.style.setProperty('--h2rv-card-w-m', `${width}px`);
      step = width + gap;
    }

    function targetFor(i) {
      return -(i * step - peek);
    }

    function setAnim(on) {
      track.classList.toggle('no-anim', !on);
    }

    function apply(x) {
      currentTranslate = x;
      track.style.transform = `translate3d(${x}px,0,0)`;
    }

    function logicalFromIndex(i) {
      if (!loop) return Math.max(0, Math.min(originals.length - 1, i));
      return ((i - cloneCount) % originals.length + originals.length) % originals.length;
    }

    function unlockAfterFallback() {
      clearTimeout(unlockTimer);
      unlockTimer = setTimeout(() => {
        animating = false;
        normalizeLoop();
      }, speed + 140);
    }

    function snap(i, animate = true) {
      const all = slides();
      if (!all[i]) return;
      index = i;
      logical = logicalFromIndex(index);
      updateDots();
      setAnim(animate);
      animating = animate;
      requestAnimationFrame(() => {
        apply(targetFor(index));
        if (animate) unlockAfterFallback();
      });
      if (!animate) {
        requestAnimationFrame(() => setAnim(true));
      }
    }

    function normalizeLoop() {
      if (!loop) return;
      let normalized = index;
      if (index < cloneCount) normalized = index + originals.length;
      if (index >= cloneCount + originals.length) normalized = index - originals.length;
      if (normalized !== index) {
        index = normalized;
        logical = logicalFromIndex(index);
        setAnim(false);
        apply(targetFor(index));
        void track.offsetWidth;
        setAnim(true);
        updateDots();
      }
    }

    function nextOne() {
      if (animating || dragging) return;
      snap(index + 1, true);
    }

    function prevOne() {
      if (animating || dragging) return;
      snap(index - 1, true);
    }

    function goLogical(target) {
      target = Math.max(0, Math.min(originals.length - 1, target));
      if (!loop) {
        snap(target, true);
        return;
      }

      const current = logical;
      let delta = target - current;
      if (Math.abs(delta) > originals.length / 2) {
        delta += delta > 0 ? -originals.length : originals.length;
      }
      if (delta === 0) return;
      snap(index + delta, true);
    }

    function onTransitionEnd(event) {
      if (event.target !== track || event.propertyName !== 'transform') return;
      clearTimeout(unlockTimer);
      animating = false;
      normalizeLoop();
      schedule();
    }

    function stop() {
      if (timer) clearTimeout(timer);
      timer = null;
      clearProgress();
    }

    function schedule() {
      stop();
      if (!autoplay || dragging || document.hidden) return;
      startProgress(delay);
      timer = setTimeout(() => {
        clearProgress();
        nextOne();
        // transitionend normally reschedules. Fallback handles environments where it doesn't.
        if (!animating) schedule();
      }, delay);
    }

    prev?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      stop();
      prevOne();
      setTimeout(schedule, speed + 160);
    });

    next?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      stop();
      nextOne();
      setTimeout(schedule, speed + 160);
    });

    track.addEventListener('transitionend', onTransitionEnd);

    viewport.addEventListener('pointerdown', event => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (originals.length < 2) return;
      stop();
      dragging = true;
      startX = lastX = event.clientX;
      startTranslate = currentTranslate;
      viewport.classList.add('is-dragging');
      setAnim(false);
      try { viewport.setPointerCapture(event.pointerId); } catch (_) {}
    });

    viewport.addEventListener('pointermove', event => {
      if (!dragging) return;
      lastX = event.clientX;
      apply(startTranslate + (lastX - startX));
    });

    function endDrag(event) {
      if (!dragging) return;
      const dx = lastX - startX;
      dragging = false;
      viewport.classList.remove('is-dragging');
      setAnim(true);
      try { viewport.releasePointerCapture(event.pointerId); } catch (_) {}
      if (Math.abs(dx) > Math.min(90, step * 0.16)) {
        dx < 0 ? nextOne() : prevOne();
      } else {
        snap(index, true);
      }
      setTimeout(schedule, speed + 160);
    }

    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);

    if (pauseHover) {
      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', schedule);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', schedule);
    }

    document.addEventListener('visibilitychange', () => {
      document.hidden ? stop() : schedule();
    });

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        measure();
        snap(index, false);
      }, 120);
    });

    document.addEventListener('shopify:block:select', event => {
      if (!root.contains(event.target)) return;
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      const i = originals.findIndex(el => el.dataset.blockId === blockId);
      if (i >= 0) goLogical(i);
    });

    makeDots();
    measure();
    snap(index, false);
    // Start the actual reference motion automatically.
    setTimeout(schedule, 350);
  }

  function initAll(scope = document) {
    scope.querySelectorAll?.(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
